"use strict";
/**
 * Pad 2's reload reconciliation (ai/nba/N8-taps/PLAN.md §19a.4): the game's published record,
 * loaded page by page and folded only when complete, and the taps the fold reads from it, the
 * queue's acknowledged taps and its unsent ones. Nothing here is on the order path.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.NBA_ACK_KEEP_MS = exports.NbaRecordLoader = void 0;
exports.nbaRecordTaps = nbaRecordTaps;
exports.nbaReloadTaps = nbaReloadTaps;
exports.nbaLoadingTaps = nbaLoadingTaps;
const nba_pad_v2_cues_1 = require("./nba-pad-v2-cues");
/**
 * Loads the record until complete. The caller drives retries (the flush timer, `online`,
 * `visibilitychange`); there is no cap and no timer here. A failure resumes from the last page
 * received: publication numbers are never reassigned, so earlier pages stay true.
 */
class NbaRecordLoader {
    constructor(fetchPage) {
        this.fetchPage = fetchPage;
        this.running = false;
        /** Wants a complete record it does not have yet (start, or refresh). */
        this.wanted = false;
        this.inflight = false;
        /** Bumped by stop: an answer from before it is ignored. */
        this.run = 0;
        /** Every row received, ascending; `completeCount` of them are the last complete record. */
        this.received = [];
        this.completeCount = 0;
        this.everComplete = false;
        this.error = null;
        this.failures = 0;
        this.listeners = new Set();
    }
    get state() {
        const rows = this.received.slice(0, this.completeCount);
        return {
            status: this.everComplete ? "complete" : this.running || this.wanted ? "loading" : "idle",
            rows,
            maxSeq: rows.length ? rows[rows.length - 1].publishedSeq : 0,
            error: this.error,
            failures: this.failures,
        };
    }
    /** Called on each change; returns the unsubscribe. */
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    start() {
        if (this.running)
            return;
        this.running = true;
        if (!this.everComplete)
            this.wanted = true;
        this.emit();
        void this.attempt();
    }
    stop() {
        this.running = false;
        this.inflight = false;
        this.run += 1;
    }
    /** Another attempt now, if a record is still wanted and none is in flight. */
    retry() {
        void this.attempt();
    }
    /** Reads what was published since; the last complete record stands until the new one is complete. */
    refresh() {
        this.wanted = true;
        void this.attempt();
    }
    async attempt() {
        if (!this.running || !this.wanted || this.inflight)
            return;
        this.inflight = true;
        const run = this.run;
        try {
            for (;;) {
                const after = this.received.length ? this.received[this.received.length - 1].publishedSeq : 0;
                const page = await this.fetchPage(after);
                if (run !== this.run)
                    return;
                let last = after;
                for (const r of page.rows) {
                    if (!(r.publishedSeq > last))
                        throw new Error(`record page out of order at ${r.publishedSeq} after ${last}`);
                    last = r.publishedSeq;
                }
                if (!page.complete && page.rows.length === 0)
                    throw new Error(`record page after ${after} did not advance`);
                this.received.push(...page.rows);
                if (page.complete)
                    break;
            }
            this.completeCount = this.received.length;
            this.everComplete = true;
            this.wanted = false;
            this.error = null;
            this.failures = 0;
        }
        catch (e) {
            if (run !== this.run)
                return;
            this.error = e.message;
            this.failures += 1;
        }
        finally {
            if (run === this.run)
                this.inflight = false;
        }
        this.emit();
    }
    emit() {
        const s = this.state;
        for (const l of [...this.listeners]) {
            try {
                l(s);
            }
            catch {
                // a listener's failure must not stop the load or the others
            }
        }
    }
}
exports.NbaRecordLoader = NbaRecordLoader;
const key = (tapId) => tapId.toLowerCase();
/**
 * The taps the fold reads (§19a.4): the record and the acknowledged taps by publishedSeq, then the
 * unsent ones in queue order, one per tap id. A parked id drops only the local copy: in the
 * acknowledged and unsent sets. A record row is never dropped; under a conflicted id it is the
 * server's own, different tap, and real (§19c).
 */
function nbaRecordTaps(record, acknowledged, unsent, parked) {
    const local = new Set([...parked].map(key));
    const seen = new Set();
    const published = [];
    for (const r of record) {
        if (seen.has(key(r.tapId)))
            continue;
        seen.add(key(r.tapId));
        published.push({ tap: r, seq: r.publishedSeq });
    }
    for (const a of acknowledged.values()) {
        const k = key(a.tap.tapId);
        if (seen.has(k) || local.has(k))
            continue;
        seen.add(k);
        published.push({ tap: a.tap, seq: a.publishedSeq });
    }
    published.sort((a, b) => a.seq - b.seq); // stable: the record's row ahead on a tie
    const out = published.map((p) => p.tap);
    for (const t of unsent) {
        const k = key(t.tapId);
        if (seen.has(k) || local.has(k))
            continue;
        seen.add(k);
        out.push(t);
    }
    return out;
}
/**
 * An acknowledgement the record already holds is kept this long after its tap (24 s shot window
 * plus slack): a reload before the next record arrives still finds the shot up (§19e).
 */
exports.NBA_ACK_KEEP_MS = 2 * nba_pad_v2_cues_1.NBA_SHOT_UP_MS;
/**
 * The fold's taps for a complete record, then the acknowledgements it holds confirmed: call it
 * only with a complete record (the loader's state once `complete`).
 */
function nbaReloadTaps(queue, record, nowMs = Date.now()) {
    const taps = nbaRecordTaps(record.rows, queue.acknowledged, queue.unsent, queue.parkedIds);
    queue.confirmAcknowledged(record.maxSeq, (a) => Date.parse(a.tap.clientTs) > nowMs - exports.NBA_ACK_KEEP_MS);
    return taps;
}
/**
 * The fold's taps while the record loads (§19e): what this phone stored for the game (acknowledged,
 * then unsent; parked out), then this pad's own taps not among them. Confirms nothing.
 */
function nbaLoadingTaps(queue, own) {
    const parked = queue.parkedIds;
    const taps = nbaRecordTaps([], queue.acknowledged, queue.unsent, parked);
    return nbaRecordTaps([], new Map(), [...taps, ...own], parked);
}
